(() => {
// -- Constants
const MOMAX_SURVEY_REVISION = '2026-07-15';

// -- Functions
class MomaxSurvey extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    // -- Inputs and outputs
    this.config = JSON.parse(this.querySelector('[data-survey-config]').textContent);
    this.panel = this.querySelector('.momax-survey__panel');
    this.mount = this.querySelector('.momax-survey__mount');
    this.dialog = this.querySelector('.momax-survey__dialog');
    this.repeat = this.querySelector('.momax-survey__repeat');
    this.steps = [...this.querySelectorAll('[data-survey-step]')];
    this.nextButton = this.querySelector('[data-next]');
    this.backButton = this.querySelector('[data-back]');
    this.error = this.querySelector('[data-submit-error]');
    this.mobile = matchMedia('(max-width: 749px)');
    // -- State and variables
    this.controller = new AbortController();
    this.index = 0;
    this.resumeIndex = 1;
    this.busy = false;
    this.submitted = false;
    this.storageKey = `momax-survey:${this.dataset.sectionId}:submitted`;
    try { this.submitted = sessionStorage.getItem(this.storageKey) === 'true'; } catch { /* Storage can be unavailable in private contexts. */ }
    this.steps.forEach((step) => this.prepareStep(step));
    this.querySelectorAll('[data-mapping="consent"], [data-mapping="email"]').forEach(input => { input.required = true; });
    this.updateProduct();
    // -- Listeners
    const options = { signal: this.controller.signal };
    this.addEventListener('click', event => this.onClick(event), options);
    this.addEventListener('input', event => this.onInput(event), options);
    this.addEventListener('change', event => this.onInput(event), options);
    this.addEventListener('focusout', event => {
      if (event.target.matches('[data-input]')) this.validateField(event.target, true);
    }, options);
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeMobile(); }, options);
    this.repeat.addEventListener('close', () => this.releaseScroll(), options);
    this.mobile.addEventListener('change', () => {
      const shown = this.steps.find(step => !step.hidden);
      if (!this.mobile.matches && this.dialog.open) {
        this.dialog.close(); this.mount.append(this.panel); this.mount.style.minHeight = ''; this.releaseScroll();
      } else if (this.mobile.matches && this.activeSteps().indexOf(shown) > 0) this.openMobile();
      this.syncIndex(shown);
      this.render();
    }, options);
    document.addEventListener('shopify:block:select', event => {
      if (!this.contains(event.target)) return;
      const step = event.target.closest('[data-survey-step]');
      if (!step) return;
      this.syncIndex(step);
      if (this.mobile.matches && this.index > 0) this.openMobile();
      else if (this.dialog.open) this.closeMobile();
      this.render();
      event.target.closest('[data-option]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, options);
    document.addEventListener('shopify:block:deselect', event => {
      if (!this.contains(event.target)) return;
      const shown = this.steps.find(step => !step.hidden);
      this.syncIndex(shown);
      this.render();
    }, options);
    this.render();
    this.dataset.ready = 'true';
  }

  prepareStep(step) {
    const products = step.dataset.presentation === 'products';
    step.querySelectorAll('[data-choice]').forEach((input, i) => {
      input.type = products || step.dataset.mode === 'single' ? 'radio' : 'checkbox';
      input.name = `${this.id}-${step.dataset.blockId}`;
      input.checked = products && i === 0;
    });
    if (!products) return;
    const track = step.querySelector('.momax-survey__options');
    const cards = [...track.querySelectorAll('[data-option]')];
    // Reuse page wrappers when the Theme Editor reconnects an existing element.
    track.replaceChildren();
    for (let i = 0; i < cards.length; i += 4) {
      const page = document.createElement('div');
      page.className = 'momax-survey__product-page';
      page.append(...cards.slice(i, i + 4)); track.append(page);
    }
    const dots = step.querySelector('.momax-survey__dots');
    dots.replaceChildren(); dots.hidden = cards.length <= 4;
    [...track.children].forEach((page, i) => {
      const dot = document.createElement('button');
      dot.type = 'button'; dot.className = 'momax-survey__dot';
      dot.setAttribute('aria-label', this.format(this.config.page_format, { page: i + 1 }));
      dot.addEventListener('click', () => track.scrollTo({ left: page.offsetLeft - track.firstElementChild.offsetLeft, behavior: 'smooth' }), { signal: this.controller.signal });
      dots.append(dot);
    });
    const updateDots = () => {
      const first = track.firstElementChild;
      if (!first) return;
      let nearest = 0, distance = Infinity;
      [...track.children].forEach((page, i) => {
        const delta = Math.abs(page.offsetLeft - first.offsetLeft - track.scrollLeft);
        if (delta < distance) { nearest = i; distance = delta; }
      });
      [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === nearest)));
    };
    track.addEventListener('scroll', updateDots, { passive: true, signal: this.controller.signal });
    updateDots();
    // Touch scroll stays native; mouse dragging also supports desktop horizontal swiping.
    let drag = null, moved = false;
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || cards.length <= 4) return;
      drag = { x: event.clientX, scroll: track.scrollLeft }; moved = false;
    }, { signal: this.controller.signal });
    track.addEventListener('pointermove', event => {
      if (!drag) return;
      const delta = event.clientX - drag.x;
      if (Math.abs(delta) > 6) {
        moved = true; track.setPointerCapture(event.pointerId);
        track.style.scrollSnapType = 'none'; track.scrollLeft = drag.scroll - delta;
      }
    }, { signal: this.controller.signal });
    const endDrag = () => { drag = null; track.style.scrollSnapType = ''; };
    track.addEventListener('pointerup', endDrag, { signal: this.controller.signal });
    track.addEventListener('pointercancel', endDrag, { signal: this.controller.signal });
    track.addEventListener('dragstart', event => event.preventDefault(), { signal: this.controller.signal });
    track.addEventListener('click', event => { if (moved) { event.preventDefault(); event.stopPropagation(); moved = false; } }, { capture: true, signal: this.controller.signal });
  }

  format(text, values) {
    return Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`[${key}]`, String(value)), text || '');
  }

  activeSteps() {
    return this.steps;
  }

  syncIndex(preferred) {
    const active = this.activeSteps();
    const shown = preferred && active.includes(preferred) ? preferred : active[0];
    this.index = Math.max(0, active.indexOf(shown));
  }

  validateField(input, show = false) {
    input.setCustomValidity('');
    const valid = input.validity.valid;
    if (show) {
      const message = input.closest('[data-field]').querySelector('.momax-survey__field-error');
      message.textContent = valid ? '' : input.dataset.error;
      message.hidden = valid;
      input.setAttribute('aria-invalid', String(!valid));
    }
    return valid;
  }

  validStep(step) {
    if (step.dataset.surveyStep === 'intro' || step.dataset.surveyStep === 'done') return true;
    if (step.dataset.surveyStep === 'choice') return Boolean(step.querySelector('[data-choice]:checked'));
    return [...step.querySelectorAll('[data-input]')].every(input => this.validateField(input));
  }

  configurationValid() {
    const emails = this.querySelectorAll('[data-mapping="email"]');
    const consents = this.querySelectorAll('[data-mapping="consent"]');
    return Boolean(this.config.public_api_key?.trim() && !/^pk_/i.test(this.config.public_api_key.trim()) && this.config.list_id?.trim()
      && emails.length === 1 && emails[0].type === 'email' && consents.length === 1 && consents[0].type === 'checkbox'
      && this.steps.length > 1 && this.steps.at(-1).dataset.surveyStep === 'done'
      && this.steps.filter(step => step.dataset.surveyStep === 'done').length === 1);
  }

  updateProduct() {
    const product = this.querySelector('[data-presentation="products"] [data-choice]:checked');
    this.productName = product?.closest('[data-option]').querySelector('.momax-survey__option-label').textContent.trim() || '';
    const media = product?.closest('[data-option]').querySelector('[data-product-media]');
    this.querySelectorAll('[data-selected-media]').forEach(container => {
      container.replaceChildren();
      if (media) container.append(media.content.cloneNode(true));
    });
  }

  render(focus = false) {
    const active = this.activeSteps();
    const current = active[this.index];
    if (!current) return;
    const done = current.dataset.surveyStep === 'done';
    const intro = current.dataset.surveyStep === 'intro';
    const last = this.index === active.length - 2;
    this.steps.forEach(step => {
      step.hidden = step !== current;
    });
    const percent = Math.round((this.index + 1) / active.length * 100);
    this.querySelector('[data-progress-text]').textContent = this.format(this.config.step_format, { current: this.index + 1, total: active.length });
    this.querySelector('[data-percent]').textContent = this.format(this.config.percent_format, { percent });
    this.querySelector('progress').value = percent;
    this.querySelector('.momax-survey__progress').hidden = done;
    this.querySelector('[data-step-label]').textContent = current.dataset.stepLabel || '';
    this.querySelector('[data-close]').hidden = !this.dialog.open;
    this.querySelector('[data-pick]').textContent = done ? (current.dataset.completionNote || '') : this.productName ? this.format(this.config.pick_format, { product: this.productName }) : '';
    this.querySelector('[data-pick]').hidden = intro || (done && !current.dataset.completionNote);
    this.querySelector('.momax-survey__footer').hidden = intro;
    this.backButton.hidden = intro || this.index === 0 || done;
    this.backButton.disabled = this.busy;
    this.nextButton.hidden = intro;
    this.nextButton.textContent = this.busy ? this.config.loading_label : done ? this.config.done_label : last ? this.config.submit_label : this.config.next_label;
    this.nextButton.disabled = this.busy || (!done && (!this.validStep(current) || (last && !this.readyToSubmit())));
    const introCta = current.querySelector('[data-intro-next]');
    if (introCta) introCta.disabled = this.busy;
    this.panel.setAttribute('aria-busy', String(this.busy));
    if (focus) { this.querySelector('.momax-survey__steps').scrollTop = 0; current.querySelector('h2')?.focus({ preventScroll: true }); }
  }

  readyToSubmit() {
    return this.configurationValid() && this.steps.slice(0, -1).every(step => this.validStep(step)) && this.querySelector('[data-mapping="consent"]').checked;
  }

  onInput(event) {
    if (event.target.matches('[data-choice]')) this.updateProduct();
    if (event.target.matches('[data-input][aria-invalid="true"]')) this.validateField(event.target, true);
    this.error.hidden = true; this.render();
  }

  onClick(event) {
    if (event.target.closest('[data-close]')) return this.closeMobile();
    if (event.target.closest('[data-repeat-no]')) return this.repeat.close();
    if (event.target.closest('[data-repeat-yes]')) {
      this.repeat.close(); this.clearAnswers(); this.advance(); return;
    }
    if (this.busy) return;
    if (event.target.closest('[data-back]')) {
      if (this.index === 1 && this.mobile.matches) { this.resumeIndex = 1; this.closeMobile(); }
      else { this.index = Math.max(0, this.index - 1); this.render(true); }
      return;
    }
    const introNext = event.target.closest('[data-intro-next]');
    if (introNext && introNext.tagName === 'A' && introNext.href) return; // 链接由浏览器处理
    if (!introNext && (!event.target.closest('[data-next]') || this.nextButton.disabled)) return;
    if (introNext && introNext.disabled) return;
    if (this.activeSteps()[this.index].dataset.surveyStep === 'done') {
      this.resumeIndex = 1;
      if (this.dialog.open) this.closeMobile();
      else { this.index = 0; this.render(true); }
      this.resumeIndex = 1;
      return;
    }
    if (this.index === 0 && this.submitted) {
      this.lockScroll(); this.repeat.showModal(); return;
    }
    this.advance();
  }

  advance() {
    const active = this.activeSteps();
    if (this.index === active.length - 2) { this.submit(); return; }
    this.index = this.index === 0 ? Math.min(this.resumeIndex, active.length - 2) : this.index + 1;
    if (this.mobile.matches) this.openMobile();
    this.error.hidden = true;
    if (this.index === active.length - 2 && !this.configurationValid()) { this.error.textContent = this.config.config_error; this.error.hidden = false; }
    this.render(true);
  }

  openMobile() {
    if (!this.dialog.open) {
      this.mount.style.minHeight = `${this.panel.offsetHeight}px`;
      this.lockScroll(); this.dialog.append(this.panel); this.dialog.showModal();
    }
    this.render();
  }

  closeMobile() {
    this.resumeIndex = this.index;
    this.dialog.close(); this.mount.append(this.panel); this.mount.style.minHeight = '';
    this.releaseScroll(); this.index = 0; this.render();
    const entryButton = this.activeSteps()[0].querySelector('[data-intro-next]') || this.nextButton;
    entryButton.focus({ preventScroll: true });
  }

  lockScroll() {
    if (this.scrollState) return;
    const body = document.body;
    this.scrollState = { y: window.scrollY, position: body.style.position, top: body.style.top, width: body.style.width };
    body.style.position = 'fixed'; body.style.top = `-${this.scrollState.y}px`; body.style.width = '100%';
  }

  releaseScroll() {
    if (!this.scrollState || this.dialog.open || this.repeat.open) return;
    const { y, position, top, width } = this.scrollState;
    Object.assign(document.body.style, { position, top, width }); this.scrollState = null; window.scrollTo(0, y);
  }

  clearAnswers() {
    this.steps.forEach(step => {
      if (step.dataset.presentation !== 'products') step.querySelectorAll('[data-choice]').forEach(input => { input.checked = false; });
      step.querySelectorAll('[data-input]').forEach(input => {
        if (input.type === 'checkbox') input.checked = false; else input.value = '';
        input.removeAttribute('aria-invalid'); input.closest('[data-field]').querySelector('.momax-survey__field-error').hidden = true;
      });
    });
    this.resumeIndex = 1;
  }

  payload() {
    const properties = Object.create(null);
    this.steps.forEach(step => {
      if (step.dataset.surveyStep !== 'choice') return;
      const selected = [...step.querySelectorAll('[data-choice]:checked')].map(input => input.value);
      properties[step.dataset.key || step.dataset.blockId] = step.dataset.mode === 'multiple' && step.dataset.presentation !== 'products' ? selected : selected[0];
    });
    this.querySelectorAll('[data-input]').forEach(input => {
      if (input.dataset.mapping === 'email') return;
      properties[input.dataset.key || input.closest('[data-field]').dataset.blockId] = input.type === 'checkbox' ? input.checked : input.value.trim();
    });
    return { data: { type: 'subscription', attributes: { custom_source: this.config.source, profile: { data: { type: 'profile', attributes: {
      email: this.querySelector('[data-mapping="email"]').value.trim(), properties,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    } } } }, relationships: { list: { data: { type: 'list', id: this.config.list_id.trim() } } } } };
  }

  async submit() {
    if (this.busy || !this.readyToSubmit()) return;
    const body = JSON.stringify(this.payload());
    this.busy = true; this.error.hidden = true;
    this.querySelectorAll('[data-input], [data-choice]').forEach(input => { input.disabled = true; });
    this.render();
    this.requestController = new AbortController();
    const timer = setTimeout(() => this.requestController.abort(), 20000);
    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.config.public_api_key.trim())}`, {
        method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: MOMAX_SURVEY_REVISION },
        body, signal: this.requestController.signal
      });
      if (response.status !== 202) throw new Error('Subscription was not accepted');
      this.submitted = true;
      try { sessionStorage.setItem(this.storageKey, 'true'); } catch { /* Keep the in-memory marker when sessionStorage is blocked. */ }
      if (this.isConnected) {
        // An explicit close during the request must not reopen the dialog.
        if (this.mobile.matches && !this.dialog.open) { this.resumeIndex = this.activeSteps().length - 1; this.index = 0; }
        else this.index = this.activeSteps().length - 1;
      }
    } catch {
      if (this.isConnected) { this.error.textContent = this.config.error_text; this.error.hidden = false; }
    } finally {
      clearTimeout(timer); this.busy = false;
      this.querySelectorAll('[data-input], [data-choice]').forEach(input => { input.disabled = false; });
      if (this.isConnected) this.render(true);
    }
  }

  // -- Lifecycle hooks
  disconnectedCallback() {
    this.requestController?.abort(); this.controller?.abort(); this.controller = null;
    this.dialog?.close(); this.repeat?.close(); this.releaseScroll();
  }
}

// -- Lifecycle hooks
if (!customElements.get('momax-survey')) customElements.define('momax-survey', MomaxSurvey);
})();
